import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Star } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "all", label: "All Units" },
  { key: "male", label: "Male" },
  { key: "female", label: "Female" },
];

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Could not load the collection"))
      .finally(() => setLoading(false));
  }, []);

  const visible =
    filter === "all" ? products : products.filter((p) => p.gender === filter);

  const handleAdd = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.name} added to your cart`, {
      description: "Discreet, unmarked packaging included.",
    });
  };

  return (
    <section id="catalog" className="px-4 sm:px-8 lg:px-16 py-20 lg:py-32">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
        <div>
          <p className="font-mono-tech text-xs uppercase tracking-[0.35em] text-cyber mb-4">
            01 // The Collection
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight">
            Choose your <span className="text-magma">companion</span>
          </h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="catalog-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}-btn`}
              onClick={() => setFilter(f.key)}
              className={`font-mono-tech text-xs uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors duration-300 ${
                filter === f.key
                  ? "border-magma text-magma bg-magma/10"
                  : "border-white/12 text-slate-400 hover:border-white/35 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="font-mono-tech text-xs uppercase tracking-[0.3em] text-slate-500 py-24 text-center" data-testid="catalog-loading">
          Initializing units…
        </div>
      ) : (
        <div
          data-testid="product-catalog-grid"
          className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8"
        >
          {visible.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className={
                i % 5 === 0
                  ? "col-span-12 md:col-span-7"
                  : i % 5 === 1
                  ? "col-span-12 md:col-span-5"
                  : "col-span-12 md:col-span-4"
              }
            >
              <Link to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`}>
                <div className="spotlight-card group h-full flex flex-col">
                  <div className="relative overflow-hidden aspect-[4/5]">
                    <img
                      src={p.image}
                      alt={`Aetheria ${p.name} companion`}
                      loading="lazy"
                      className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 font-mono-tech text-[10px] uppercase tracking-[0.25em] bg-black/70 backdrop-blur-md border border-magma/40 text-magma px-3 py-1.5">
                      {p.badge}
                    </span>
                    <button
                      data-testid={`quick-add-${p.slug}`}
                      onClick={(e) => handleAdd(e, p)}
                      aria-label={`Add ${p.name} to cart`}
                      className="absolute bottom-4 right-4 w-11 h-11 bg-magma text-white flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 glow-magenta hover:bg-[#ff4d87]"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                  <div className="p-6 flex-1 flex flex-col justify-between gap-3">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-display text-xl font-bold uppercase tracking-tight">
                          {p.name}
                        </h3>
                        <span className="flex items-center gap-1 font-mono-tech text-xs text-cyber">
                          <Star size={11} fill="currentColor" />
                          {p.rating}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400 leading-relaxed">{p.tagline}</p>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-white/8">
                      <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-slate-500">
                        {p.gender} unit
                      </span>
                      <span className="flex items-baseline gap-2">
                        {p.compare_at && (
                          <span className="font-mono-tech text-xs text-slate-500 line-through">
                            {p.compare_at}
                          </span>
                        )}
                        <span className="font-display text-lg font-bold text-magma">
                          {p.price}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}
